import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faSun, faMoon, faMobileAlt, faCheckCircle, faSpinner, faShieldAlt, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import {
  faInstagram, faTwitter, faFacebook, faYoutube,
  faTiktok, faTelegram, faSpotify, faDiscord, faSoundcloud,
} from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import CountUp from './CountUp';
import './AuthLanding.css';
import './Register.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';
const OTP_RESEND_SECONDS = 60;
const icons = [faInstagram, faTwitter, faFacebook, faYoutube, faTelegram, faSpotify, faDiscord, faSoundcloud];

export default function Register() {
  const { register } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  // Form state
  const [form, setForm] = useState({ name: '', username: '', email: '', phone: '', password: '', password_confirmation: '' });
  const [agree, setAgree] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // OTP state
  const [step, setStep] = useState('form');   // 'form' | 'otp'
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);
  const [resendsLeft, setResendsLeft] = useState(1);
  const countdownRef = useRef(null);

  // UI state
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  // ── Countdown timer ──────────────────────────────────────────────────────
  const startCountdown = () => {
    setCountdown(OTP_RESEND_SECONDS);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(countdownRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  useEffect(() => () => { if (countdownRef.current) clearInterval(countdownRef.current); }, []);

  // ── Step 1: validate form & send OTP ─────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');

    if (!agree) {
      setError('You must agree to the Terms & Conditions and Privacy Policy.');
      return;
    }
    if (form.password !== form.password_confirmation) {
      setError('Passwords do not match.');
      return;
    }
    if (!form.phone) {
      setError('Phone number is required.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: form.phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.resends_left !== undefined) setResendsLeft(data.resends_left);
        throw new Error(data.message || 'Failed to send OTP.');
      }
      if (data.resends_left !== undefined) {
        setResendsLeft(data.resends_left);
      }
      setStep('otp');
      setInfo(`A 5-digit verification code was sent to ${form.phone}`);
      startCountdown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Resend OTP ────────────────────────────────────────────────────────────
  const handleResend = async () => {
    if (countdown > 0 || resendsLeft <= 0) return;
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await fetch(`${API}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: form.phone }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.resends_left !== undefined) setResendsLeft(data.resends_left);
        throw new Error(data.message || 'Failed to resend OTP.');
      }
      if (data.resends_left !== undefined) {
        setResendsLeft(data.resends_left);
      }
      setInfo('A new code was sent to your phone.');
      startCountdown();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Verify phase state: idle | scanning | success | error ────────────────
  const [verifyPhase, setVerifyPhase] = useState('idle');

  // ── Step 2: verify OTP & create account ──────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (otp.length !== 5) {
      setError('Please enter the 5-digit code sent to your phone.');
      return;
    }

    // Kick off scanning animation
    setVerifyPhase('scanning');
    setLoading(true);

    try {
      await register({ ...form, otp });
      // Brief success flash before navigating
      setVerifyPhase('success');
      setTimeout(() => navigate('/'), 1800);
    } catch (err) {
      setVerifyPhase('error');
      setTimeout(() => {
        setVerifyPhase('idle');
        setLoading(false);
        setError(err.message);
      }, 1200);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="auth-landing">
      <div className="auth-floating-icons">
        {icons.map((icon, i) => (
          <FontAwesomeIcon key={i} icon={icon} className="floating-icon" />
        ))}
      </div>

      <nav className="auth-nav">
        <div className="auth-nav-inner">
          <div className="auth-nav-logo">
            <img src="/Black and Yellow Online Store Logo.png" alt="bustisha" />
            <span>bustisha</span>
          </div>
          <ul className="auth-nav-links">
            <li><Link to="/login">Sign in</Link></li>
            <li><Link to="/register">Sign up</Link></li>
            <li>
              <button className="auth-theme-btn" onClick={toggle}>
                <FontAwesomeIcon icon={theme === 'light' ? faMoon : faSun} />
              </button>
            </li>
          </ul>
        </div>
      </nav>

      <section className="auth-hero">
        <div className="auth-hero-left fade-in-left">
          <h1>Boost Your Social <br />Media Presence</h1>
          <h4>with <span>bustisha</span> SMM Panel</h4>
          <p>
            Our SMM panel helps you increase your social media presence and reach.
            Easy to use, fast delivery, and affordable prices for everyone.
          </p>
        </div>

        <div className="auth-form-card fade-in-right">
          <div className="auth-form-card-header">
            <img src="/Black and Yellow Online Store Logo.png" alt="bustisha" />
            <h3>Create Your Account</h3>
          </div>

          {error && <div className="auth-error">{error}</div>}
          {info  && <div className="auth-info">{info}</div>}

          {/* ── STEP 1: Registration form ── */}
          {step === 'form' && (
            <form onSubmit={handleSendOtp}>
              <div className="auth-field-row">
                <div className="auth-field">
                  <input type="text" placeholder="Full name" value={form.name} onChange={set('name')} required />
                </div>
                <div className="auth-field">
                  <input type="text" placeholder="Username" value={form.username} onChange={set('username')} required />
                </div>
              </div>
              <div className="auth-field">
                <input type="email" placeholder="Email address" value={form.email} onChange={set('email')} required />
              </div>
              <div className="auth-field otp-phone-field">
                <FontAwesomeIcon icon={faMobileAlt} className="otp-phone-icon" />
                <input
                  type="tel"
                  placeholder="Phone number e.g. 255712345678"
                  value={form.phone}
                  onChange={set('phone')}
                  required
                />
              </div>
              <div className="auth-field-row">
                <div className="auth-field auth-pw-wrap">
                  <input type={showPw ? 'text' : 'password'} placeholder="Password" value={form.password} onChange={set('password')} required minLength={6} />
                </div>
                <div className="auth-field auth-pw-wrap">
                  <input type={showPw ? 'text' : 'password'} placeholder="Confirm" value={form.password_confirmation} onChange={set('password_confirmation')} required minLength={6} />
                </div>
              </div>
              <div className="auth-pw-wrap" style={{ marginBottom: 14, textAlign: 'right' }}>
                <button type="button" className="auth-pw-toggle" style={{ position: 'static', transform: 'none' }} onClick={() => setShowPw(!showPw)}>
                  <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} /> {showPw ? 'Hide' : 'Show'} passwords
                </button>
              </div>
              <label className="auth-agree">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
                <span>I agree to the <Link to="/terms" target="_blank">Terms &amp; Conditions</Link> and <Link to="/privacy" target="_blank">Privacy Policy</Link></span>
              </label>
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Sending code...</>
                  : <><FontAwesomeIcon icon={faMobileAlt} /> Verify Phone &amp; Continue</>}
              </button>
            </form>
          )}

          {/* ── STEP 2: OTP verification ── */}
          {step === 'otp' && (
            <form onSubmit={handleRegister} className="otp-verify-form">

              {/* ── Verification overlay ── */}
              {verifyPhase !== 'idle' && (
                <div className={`otp-overlay otp-overlay--${verifyPhase}`}>
                  <div className="otp-overlay-card">

                    {/* SVG spinning ring */}
                    <div className="otp-ring-wrap">
                      <svg className="otp-ring-svg" viewBox="0 0 100 100">
                        <circle className="otp-ring-track" cx="50" cy="50" r="44" />
                        <circle
                          className={`otp-ring-progress otp-ring-progress--${verifyPhase}`}
                          cx="50" cy="50" r="44"
                        />
                      </svg>

                      {/* Centre icon */}
                      <div className={`otp-ring-icon otp-ring-icon--${verifyPhase}`}>
                        {verifyPhase === 'success'
                          ? <FontAwesomeIcon icon={faCheckCircle} />
                          : verifyPhase === 'error'
                          ? <FontAwesomeIcon icon={faTimesCircle} />
                          : <FontAwesomeIcon icon={faShieldAlt} className="otp-shield-pulse" />
                        }
                      </div>
                    </div>

                    {/* Scanned digits display */}
                    {verifyPhase === 'scanning' && (
                      <div className="otp-scan-digits">
                        {otp.split('').map((d, i) => (
                          <span key={i} className="otp-scan-digit" style={{ animationDelay: `${i * 0.12}s` }}>
                            {d}
                          </span>
                        ))}
                      </div>
                    )}

                    <p className={`otp-overlay-label otp-overlay-label--${verifyPhase}`}>
                      {verifyPhase === 'scanning' && 'Verifying code...'}
                      {verifyPhase === 'success'  && 'Account created!'}
                      {verifyPhase === 'error'    && 'Verification failed'}
                    </p>

                    {/* Bouncing dots during scan */}
                    {verifyPhase === 'scanning' && (
                      <div className="otp-dots">
                        <span /><span /><span />
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="otp-icon-wrap">
                <FontAwesomeIcon icon={faCheckCircle} className="otp-check-icon" />
              </div>
              <p className="otp-hint">Enter the 5-digit code sent to <strong>{form.phone}</strong></p>
              <div className={`auth-field otp-input-wrap${verifyPhase === 'error' ? ' otp-input-shake' : ''}`}>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  placeholder="_ _ _ _ _"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 5))}
                  className="otp-input"
                  autoFocus
                  required
                />
              </div>
              <button type="submit" className="auth-btn" disabled={loading}>
                {loading
                  ? <><FontAwesomeIcon icon={faSpinner} spin /> Creating account...</>
                  : 'Create Account'}
              </button>
              <div className="otp-resend">
                {countdown > 0 ? (
                  <span className="otp-countdown">Resend code in {countdown}s</span>
                ) : resendsLeft > 0 ? (
                  <button type="button" className="otp-resend-btn" onClick={handleResend} disabled={loading}>
                    Resend code ({resendsLeft} {resendsLeft === 1 ? 'chance' : 'chances'} left)
                  </button>
                ) : (
                  <span className="otp-limit-reached">
                    ⚠️ Resend limit reached. Please wait 3 hours to request a new code.
                  </span>
                )}
              </div>
              <button type="button" className="otp-back-btn" onClick={() => { setStep('form'); setOtp(''); setError(''); setInfo(''); setVerifyPhase('idle'); }}>
                ← Change phone number
              </button>
            </form>
          )}


          <p className="auth-form-footer">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </section>

      <section className="auth-services fade-in-up">
        <div className="auth-services-inner">
          <h2 className="auth-section-title">Top SMM Services</h2>
          <p className="auth-section-sub">We provide high-quality services for all major platforms</p>
          <div className="auth-platforms-grid">
            {[
              { name: 'Instagram', icon: faInstagram, color: '#E4405F', services: 5, from: '500' },
              { name: 'YouTube', icon: faYoutube, color: '#FF0000', services: 3, from: '500' },
              { name: 'TikTok', icon: faTiktok, color: '#000000', services: 3, from: '300' },
              { name: 'Twitter', icon: faTwitter, color: '#1DA1F2', services: 2, from: '600' },
              { name: 'Telegram', icon: faTelegram, color: '#26A5E4', services: 1, from: '4,000' },
              { name: 'Facebook', icon: faFacebook, color: '#1877F2', services: 1, from: '3,000' },
              { name: 'Spotify', icon: faSpotify, color: '#1DB954', services: 1, from: '1,500' },
              { name: 'Discord', icon: faDiscord, color: '#5865F2', services: 2, from: '6,000' },
            ].map((p) => (
              <div key={p.name} className="auth-platform-card" style={{ '--plat-color': p.color }}>
                <div className="auth-platform-icon" style={{ background: `${p.color}15` }}>
                  <FontAwesomeIcon icon={p.icon} style={{ color: p.color }} />
                </div>
                <h4>{p.name}</h4>
                <p className="auth-platform-services">{p.services} services</p>
                <p className="auth-platform-from">From TSHS {p.from}/1K</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="auth-stats fade-in-up">
        <div className="auth-stats-grid">
          {[
            { label: 'Total Orders', value: '32245386' },
            { label: 'Starting Price', value: null, text: '$0.001' },
            { label: 'Fastest Support', value: null, text: '24/7' },
            { label: 'Happy Clients', value: '14509', suffix: '+' },
          ].map((s) => (
            <div key={s.label} className="auth-stat-item">
              <h4>{s.value != null ? <CountUp end={s.value} suffix={s.suffix || ''} /> : s.text}</h4>
              <p>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="auth-footer">
        &copy; 2026 bustisha | All Rights Reserved
        <span className="auth-footer-links">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
        </span>
      </footer>
    </div>
  );
}
